/** Dutch postcode: 4 digits + optional space + 2 letters (e.g. 1234 AB or 1234AB). */
export const DUTCH_POSTCODE_REGEX = /^\d{4}\s?[A-Za-z]{2}$/;

export const DUTCH_POSTCODE_ERROR =
  "Use Dutch postcode format: 1234 AB or 1234AB";

/** Broad international format: letters, numbers, spaces, hyphens (2-12 chars). */
export const INTERNATIONAL_POSTCODE_REGEX =
  /^[A-Za-z0-9][A-Za-z0-9\s-]{0,10}[A-Za-z0-9]$/;

export const INTERNATIONAL_POSTCODE_ERROR =
  "Enter a valid postcode (2-12 letters, numbers, spaces, or hyphens)";

const NETHERLANDS_COUNTRY_ALIASES = new Set([
  "netherlands",
  "nederland",
  "nl",
  "the netherlands",
]);

export const isNetherlandsCountry = (country?: string) => {
  if (!country?.trim()) return false;
  return NETHERLANDS_COUNTRY_ALIASES.has(country.trim().toLowerCase());
};

export const isValidDutchPostcode = (value: string) =>
  DUTCH_POSTCODE_REGEX.test(value.trim());

export const isValidInternationalPostcode = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.length < 2 || trimmed.length > 12) return false;
  return INTERNATIONAL_POSTCODE_REGEX.test(trimmed);
};

export const isValidPostcode = (value: string, country?: string) => {
  const trimmed = value.trim();
  if (!trimmed) return false;

  if (isNetherlandsCountry(country)) {
    return isValidDutchPostcode(trimmed);
  }

  return isValidInternationalPostcode(trimmed);
};

export const getPostcodeError = (country?: string) =>
  isNetherlandsCountry(country)
    ? DUTCH_POSTCODE_ERROR
    : INTERNATIONAL_POSTCODE_ERROR;

export const getPostcodePlaceholder = (country?: string) =>
  isNetherlandsCountry(country) ? "1234 AB" : "Enter postcode";

export const formatDutchPostcodeInput = (raw: string): string => {
  const cleaned = raw.toUpperCase().replace(/[^0-9A-Z]/g, "");
  let digitPart = "";
  let letterPart = "";

  for (const char of cleaned) {
    if (digitPart.length < 4 && /[0-9]/.test(char)) {
      digitPart += char;
      continue;
    }

    if (digitPart.length === 4 && /[A-Z]/.test(char) && letterPart.length < 2) {
      letterPart += char;
    }
  }

  if (digitPart.length < 4) {
    return digitPart;
  }

  if (!letterPart) {
    return digitPart;
  }

  return `${digitPart} ${letterPart}`;
};

export const formatInternationalPostcodeInput = (raw: string): string =>
  raw.replace(/[^A-Za-z0-9\s-]/g, "").slice(0, 12);

export const formatPostcodeInput = (raw: string, country?: string) =>
  isNetherlandsCountry(country)
    ? formatDutchPostcodeInput(raw)
    : formatInternationalPostcodeInput(raw);

export const normalizeDutchPostcode = (value: string) => {
  const compact = value.trim().toUpperCase().replace(/\s+/g, "");

  if (/^\d{4}[A-Z]{2}$/.test(compact)) {
    return `${compact.slice(0, 4)} ${compact.slice(4)}`;
  }

  return value.trim().toUpperCase();
};

export const normalizePostcode = (value: string, country?: string) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (isNetherlandsCountry(country) && isValidDutchPostcode(trimmed)) {
    return normalizeDutchPostcode(trimmed);
  }

  return trimmed;
};
